import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, urlImagem } from '../lib/api';
import { rotuloStatus, rotuloTipo, dataHora } from '../lib/rotulos';
import Loading from '../components/Loading';
import { useAuth } from '../lib/auth';

interface OSItem { id: number; codigo: string; tipo_os: string; status: string; created_at: string; }
interface Entidade {
  id: number; nome: string; status: string;
  unidade_id?: number; piso_id?: number; local_id?: number;
  patrimonio?: string | null; marca?: string | null; fabricante?: string | null;
  modelo?: string | null; numero_serie?: string | null; foto_url?: string | null;
}
interface Resolvido {
  tipo: 'ativo' | 'local';
  entidade: Entidade;
  os_abertas: OSItem[];
  historico: OSItem[];
}

// Tela aberta ao escanear o QR de um equipamento (ATV-) ou local (LOC-).
// 1 OS aberta -> abre a OS direto | varias -> lista | nenhuma -> ficha + historico + nova OS.
export default function QRAlvo() {
  const { user } = useAuth();
  const { empresaId, codigo } = useParams();
  const nav = useNavigate();
  const [dados, setDados] = useState<Resolvido | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    setErro('');
    setDados(null);
    const e = empresaId ? '&e=' + encodeURIComponent(empresaId) : '';
    api<Resolvido>('/qr/resolver.php?codigo=' + encodeURIComponent(codigo || '') + e)
      .then((r) => {
        // 1 OS aberta -> vai direto para a execucao/consulta
        if (r.os_abertas.length === 1) {
          nav('/app/os/' + r.os_abertas[0].id, { replace: true });
          return;
        }
        setDados(r);
      })
      .catch((err) => setErro(err instanceof Error ? err.message : String(err)));
  }, [codigo, empresaId, nav]);

  if (erro) return (
    <div>
      <div className="msg-erro">{erro}</div>
      <div style={{ marginTop: 12 }}><Link className="btn sec sm" to="/app/os">Ir para as ordens de servico</Link></div>
    </div>
  );
  if (!dados) return <Loading />;

  const { tipo, entidade, os_abertas, historico } = dados;
  const ehAtivo = tipo === 'ativo';
  const podeCriarOs = !(user?.perfil === 'tecnico' && user?.tecnico_nao_pode_criar_os);
  const linkNova = ehAtivo
    ? '/app/os/nova?ativo=' + entidade.id
    : '/app/os/nova?local=' + entidade.id + '&piso=' + (entidade.piso_id || '') + '&unidade=' + (entidade.unidade_id || '');

  return (
    <div>
      <div className="titulo-pg">
        <h1>{entidade.nome} <span className="badge">{ehAtivo ? 'Equipamento' : 'Local'}</span></h1>
        <Link to="/app/os" className="btn sec sm">Ordens de servico</Link>
      </div>

      {/* Varias OS abertas -> tecnico escolhe qual acessar */}
      {os_abertas.length > 1 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h3>Ordens de servico em aberto ({os_abertas.length})</h3>
          <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>Selecione qual OS deseja acessar.</p>
          <div className="tabela-wrap" style={{ border: 0, boxShadow: 'none' }}>
            <table style={{ minWidth: 0 }}>
              <thead><tr><th>Codigo</th><th>Tipo</th><th>Status</th><th>Quando</th></tr></thead>
              <tbody>
                {os_abertas.map((o) => (
                  <tr key={o.id}>
                    <td><Link to={'/app/os/' + o.id}>{o.codigo}</Link></td>
                    <td>{rotuloTipo(o.tipo_os)}</td>
                    <td>{rotuloStatus(o.status)}</td>
                    <td>{dataHora(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid cols2">
        <div className="card">
          <h3>{ehAtivo ? 'Equipamento' : 'Local'}</h3>
          <div className="os-dados">
            {ehAtivo && <>
              <div className="os-campo"><span className="k">Patrimonio</span><span className="v">{entidade.patrimonio || '-'}</span></div>
              <div className="os-campo"><span className="k">Marca</span><span className="v">{entidade.marca || '-'}</span></div>
              <div className="os-campo"><span className="k">Fabricante</span><span className="v">{entidade.fabricante || '-'}</span></div>
              <div className="os-campo"><span className="k">Modelo</span><span className="v">{entidade.modelo || '-'}</span></div>
              <div className="os-campo"><span className="k">{user?.rotulo_numero_serie || 'Numero de serie'}</span><span className="v">{entidade.numero_serie || '-'}</span></div>
            </>}
            <div className="os-campo"><span className="k">Status</span><span className="v">{entidade.status}</span></div>
          </div>
          {ehAtivo && entidade.foto_url && (
            <a href={urlImagem(entidade.foto_url)} target="_blank" rel="noreferrer">
              <img src={urlImagem(entidade.foto_url)} alt="" className="logo-preview" />
            </a>
          )}
          {!os_abertas.length && <p style={{ color: 'var(--texto-suave)' }}>Nenhuma OS em aberto para este {ehAtivo ? 'equipamento' : 'local'}.</p>}
          {podeCriarOs && (
            <div className="form-actions">
              <Link className="btn" to={linkNova}>+ Nova OS para este {ehAtivo ? 'equipamento' : 'local'}</Link>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Historico de OS</h3>
          <div className="tabela-wrap" style={{ border: 0, boxShadow: 'none' }}>
            <table style={{ minWidth: 0 }}>
              <thead><tr><th>Codigo</th><th>Tipo</th><th>Status</th><th>Quando</th></tr></thead>
              <tbody>
                {historico.map((o) => (
                  <tr key={o.id}>
                    <td><Link to={'/app/os/' + o.id}>{o.codigo}</Link></td>
                    <td>{rotuloTipo(o.tipo_os)}</td>
                    <td>{rotuloStatus(o.status)}</td>
                    <td>{dataHora(o.created_at)}</td>
                  </tr>
                ))}
                {!historico.length && <tr><td colSpan={4} className="vazio">Nenhuma OS ainda.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
